import { useState, useEffect, useCallback, useRef } from 'react';
import type { DriveItem } from '@/mocks/driveFiles';
import { formatFileSize } from '@/mocks/driveFiles';

export interface MediaItem {
  item: DriveItem;
  src: string;
}

interface MediaPlayerProps {
  mediaFiles: MediaItem[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function MediaPlayer({ mediaFiles, currentIndex, onClose, onNavigate }: MediaPlayerProps) {
  const current = mediaFiles[currentIndex];
  const isVideo = current?.item.mimeType?.startsWith('video/') ?? false;
  const isAudio = current?.item.mimeType?.startsWith('audio/') ?? false;

  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const mediaEl = videoRef.current || audioRef.current;

  const goNext = useCallback(() => {
    if (currentIndex < mediaFiles.length - 1) {
      setLoaded(false);
      setError(false);
      setPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      onNavigate(currentIndex + 1);
    }
  }, [currentIndex, mediaFiles.length, onNavigate]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setLoaded(false);
      setError(false);
      setPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      onNavigate(currentIndex - 1);
    }
  }, [currentIndex, onNavigate]);

  const togglePlay = useCallback(() => {
    if (!mediaEl) return;
    if (mediaEl.paused) {
      mediaEl.play().catch(() => {});
    } else {
      mediaEl.pause();
    }
  }, [mediaEl]);

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!mediaEl || !progressRef.current || !duration) return;
      const rect = progressRef.current.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      mediaEl.currentTime = ratio * duration;
    },
    [mediaEl, duration]
  );

  const handleVolumeChange = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!mediaEl) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      mediaEl.volume = ratio;
      setVolume(ratio);
      setMuted(ratio === 0);
    },
    [mediaEl]
  );

  const toggleMute = useCallback(() => {
    if (!mediaEl) return;
    mediaEl.muted = !mediaEl.muted;
    setMuted(!mediaEl.muted);
  }, [mediaEl]);

  useEffect(() => {
    setLoaded(false);
    setError(false);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [currentIndex]);

  useEffect(() => {
    const el = videoRef.current || audioRef.current;
    if (!el) return;

    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onDuration = () => setDuration(el.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onLoaded = () => { setLoaded(true); setError(false); };
    const onErr = () => { setError(true); setLoaded(false); };

    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('loadedmetadata', onDuration);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('canplay', onLoaded);
    el.addEventListener('error', onErr);

    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('loadedmetadata', onDuration);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('canplay', onLoaded);
      el.removeEventListener('error', onErr);
    };
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        togglePlay();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, goPrev, goNext, togglePlay]);

  if (!current) return null;

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const volumePercent = muted ? 0 : volume * 100;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center animate-in fade-in duration-200"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/92 backdrop-blur-md"></div>

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-all duration-200 cursor-pointer"
        aria-label="닫기"
      >
        <i className="ri-close-line text-xl"></i>
      </button>

      {/* Counter */}
      {mediaFiles.length > 1 && (
        <div className="absolute top-5 left-1/2 -translate-x-1/2 z-10 px-4 py-1.5 rounded-full bg-white/10 text-white/80 text-sm font-medium backdrop-blur-sm">
          {currentIndex + 1} / {mediaFiles.length}
        </div>
      )}

      {/* Prev button */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-4 md:left-8 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-all duration-200 cursor-pointer group"
          aria-label="이전"
        >
          <i className="ri-arrow-left-s-line text-2xl group-hover:-translate-x-0.5 transition-transform"></i>
        </button>
      )}

      {/* Next button */}
      {currentIndex < mediaFiles.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-4 md:right-8 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-all duration-200 cursor-pointer group"
          aria-label="다음"
        >
          <i className="ri-arrow-right-s-line text-2xl group-hover:translate-x-0.5 transition-transform"></i>
        </button>
      )}

      {/* Media content area */}
      <div
        className="relative z-[1] flex flex-col items-center justify-center max-w-[92vw] max-h-[82vh] select-none gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Loading spinner */}
        {!loaded && !error && current.src && (
          <div className="flex items-center justify-center py-20">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5">
              <i className="ri-loader-4-line text-3xl text-white/50 animate-spin"></i>
            </span>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex flex-col items-center gap-4 text-white/50 py-16 px-8">
            <span className="w-20 h-20 flex items-center justify-center rounded-2xl bg-white/5">
              <i className={`text-4xl ${isVideo ? 'ri-video-upload-line' : 'ri-music-line'}`}></i>
            </span>
            <p className="text-sm font-medium">미디어를 불러올 수 없습니다</p>
            <p className="text-xs text-white/30">{current.item.name}</p>
          </div>
        )}

        {/* No source */}
        {!current.src && (
          <div className="flex flex-col items-center gap-4 text-white/50 py-16 px-8">
            <span className="w-20 h-20 flex items-center justify-center rounded-2xl bg-white/5">
              <i className={`text-4xl ${isVideo ? 'ri-video-line' : 'ri-music-line'}`}></i>
            </span>
            <p className="text-sm font-medium">미리보기를 지원하지 않는 파일입니다</p>
            <p className="text-xs text-white/30">{current.item.name}</p>
          </div>
        )}

        {/* Video element */}
        {isVideo && current.src && (
          <video
            ref={videoRef}
            src={current.src}
            className={`max-w-full max-h-[68vh] rounded-lg shadow-2xl ${loaded && !error ? 'opacity-100' : 'opacity-0 absolute'}`}
            preload="metadata"
            playsInline
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
          />
        )}

        {/* Audio visualizer card */}
        {isAudio && current.src && (
          <div className={`flex flex-col items-center gap-5 ${loaded && !error ? 'opacity-100' : 'opacity-0 absolute'}`}>
            {/* Album art / icon */}
            <div className="w-48 h-48 md:w-56 md:h-56 flex items-center justify-center rounded-2xl bg-white/5 overflow-hidden">
              <span className={`w-24 h-24 flex items-center justify-center transition-transform duration-300 ${playing ? 'scale-110' : 'scale-100'}`}>
                <i className={`ri-music-fill text-7xl ${playing ? 'text-white/90' : 'text-white/40'} transition-colors duration-300`}></i>
              </span>
            </div>

            {/* File name */}
            <p className="text-white/90 text-base font-medium text-center max-w-[60vw] truncate px-4">
              {current.item.name}
            </p>

            {/* Custom progress bar */}
            <div className="w-full max-w-md flex items-center gap-3 px-2">
              <span className="text-white/50 text-xs w-10 text-right tabular-nums">{formatTime(currentTime)}</span>
              <div
                ref={progressRef}
                className="flex-1 h-1.5 bg-white/15 rounded-full cursor-pointer group/progress relative"
                onClick={handleSeek}
              >
                <div
                  className="h-full bg-white/80 rounded-full transition-all duration-100 relative"
                  style={{ width: `${progressPercent}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity shadow-lg"></div>
                </div>
              </div>
              <span className="text-white/50 text-xs w-10 tabular-nums">{formatTime(duration)}</span>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-4">
              {/* Play/Pause */}
              <button
                onClick={togglePlay}
                className="w-12 h-12 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white transition-all duration-200 cursor-pointer"
                aria-label={playing ? '일시정지' : '재생'}
              >
                <i className={`text-xl ${playing ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
              </button>

              {/* Volume */}
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleMute}
                  className="w-8 h-8 flex items-center justify-center rounded-full text-white/60 hover:text-white transition-colors cursor-pointer"
                  aria-label={muted ? '음소거 해제' : '음소거'}
                >
                  <i className={`text-base ${muted || volume === 0 ? 'ri-volume-mute-line' : volume < 0.5 ? 'ri-volume-down-line' : 'ri-volume-up-line'}`}></i>
                </button>
                <div
                  className="w-20 h-1 bg-white/15 rounded-full cursor-pointer group/vol relative"
                  onClick={handleVolumeChange}
                >
                  <div
                    className="h-full bg-white/70 rounded-full transition-all duration-100 relative"
                    style={{ width: `${volumePercent}%` }}
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-white rounded-full opacity-0 group-hover/vol:opacity-100 transition-opacity shadow-lg"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-center gap-4 px-6 py-4 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
        <span className={`w-5 h-5 flex items-center justify-center ${isVideo ? 'text-primary-400' : 'text-accent-400'}`}>
          <i className={`text-sm ${isVideo ? 'ri-video-line' : 'ri-music-line'}`}></i>
        </span>
        <span className="text-white/90 text-sm font-medium max-w-[40vw] truncate">{current.item.name}</span>
        {current.item.size != null && (
          <>
            <span className="text-white/30 text-xs select-none">|</span>
            <span className="text-white/55 text-sm">{formatFileSize(current.item.size)}</span>
          </>
        )}
      </div>
    </div>
  );
}